'use client'

import { useCallback, useMemo, useState } from 'react'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import posthog from 'posthog-js'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import { calcTotalForCart } from '@booking/lib/cart'
import { stageIdOf, stageNumberOf } from '@booking/lib/bookingConfig'

import type { Cart, CartCustomer } from '@booking/types/booking'

import { useGatewayCard } from './useGatewayCard'
import { useGatewayPaypal } from './useGatewayPaypal'
import { buildPaypalReturnUrls } from '../lib/paymentUrls'
import { handleAppError } from '../../../lib/errors/handleAppError'

type PaymentMethod = 'card' | 'paypal' | 'mbway' | null

type UsePaymentFlowArgs = {
  rebuildCart: () => Promise<Cart | null | undefined>
}

function getCustomerEmail(customerData: CartCustomer): string {
  const cd = customerData
  return cd?.email ?? ''
}

export function usePaymentFlow({ rebuildCart }: UsePaymentFlowArgs) {
  const t = useTranslations('booking')
  const router = useRouter()
  const pathname = usePathname()
  const params = useParams<{ locale?: string }>()
  const locale = params?.locale ?? 'en-GB'

  const state = useBookingState()
  const { dispatch } = useBookingActionsCtx()

  const cart = state.cart
  const email = useMemo(() => getCustomerEmail(state.customerData), [state.customerData])

  const [method, setMethod] = useState<PaymentMethod>(null)
  const [hasFired, setHasFired] = useState(false)

  const total = useMemo(() => calcTotalForCart(cart), [cart])

  const actionUrl = useMemo(() => {
    const cartId = cart?.id ?? ''
    const qs = new URLSearchParams()
    if (cartId) qs.set('cartId', cartId)
    qs.set('paymentMethod', 'card')
    return `${process.env.NEXT_PUBLIC_SITE_URL}${pathname}?${qs.toString()}`
  }, [cart?.id, pathname])

  const showPaymentMethods = true //Boolean(cart && Number(cart.totalValue) > 0)
  const showFreeContinue = Boolean(cart && cart.total === 0 && cart.lines?.length > 0)

  const shouldRebuildBeforePay = useCallback(() => {
    return Boolean(cart && (cart.status === 'RESERVED' || hasFired))
  }, [cart, hasFired])

  const { startCardPayment } = useGatewayCard({ locale, email })
  const { startPaypalPayment } = useGatewayPaypal({ email })

  const onPayWithCard = useCallback(async () => {
    console.log('a')
    console.log(cart)
    setMethod('card')
    posthog.capture('payment_method_selected', { method: 'card', cart_id: cart?.id ?? null })
    if (!cart) return

    try {
      console.log('b')
      const useCart = shouldRebuildBeforePay() ? ((await rebuildCart()) as Cart) : cart
      console.log('c')
      posthog.capture('payment_initiated', {
        method: 'card',
        cart_id: useCart.id,
        cart_total: useCart.total,
      })
      await startCardPayment({ cart: useCart })
      setHasFired(true)
    } catch (e) {
      handleAppError(e, { dispatch, t })
      /*dispatch({
        type: 'SET_ERROR',
        error: { level: 'medium', message: t('ticket.payment.error') },
      })*/
    }
  }, [cart, dispatch, rebuildCart, shouldRebuildBeforePay, startCardPayment, t])

  const onPayWithPaypal = useCallback(async () => {
    if (!cart) return
    setMethod('paypal')
    posthog.capture('payment_method_selected', { method: 'paypal', cart_id: cart.id })

    try {
      const useCart = shouldRebuildBeforePay() ? ((await rebuildCart()) as Cart) : cart

      const { successUrl, errorUrl } = buildPaypalReturnUrls({ pathname, cartId: useCart.id })
      posthog.capture('payment_initiated', {
        method: 'paypal',
        cart_id: useCart.id,
        cart_total: useCart.total,
      })
      await startPaypalPayment({ cart: useCart, total, successUrl, errorUrl })

      setHasFired(true)
    } catch (e) {
      handleAppError(e, { dispatch, t })
      /*dispatch({
        type: 'SET_ERROR',
        error: { level: 'medium', message: t('ticket.payment.error') },
      })*/
    }
  }, [cart, dispatch, pathname, rebuildCart, shouldRebuildBeforePay, startPaypalPayment, t, total])

  const onPayWithMbWay = useCallback(() => {
    setMethod('mbway')
    posthog.capture('payment_method_selected', { method: 'mbway', cart_id: cart?.id ?? null })
  }, [cart])

  const onDiscountChanged = useCallback(() => {
    setMethod(null)
    setHasFired(false)
  }, [])

  const afterRebuildCart = useCallback(() => {
    setMethod(null)
    setHasFired(false)
  }, [])

  const onFreeContinue = useCallback(() => {
    if (!cart) return
    const thankyouStage = stageNumberOf(state.config, 'thankyou')
    const thankyouId = stageIdOf(state.config, thankyouStage) ?? 'thankyou'

    dispatch({ type: 'SET_STAGE', stage: thankyouStage })

    const qs = new URLSearchParams()
    qs.set('stage', thankyouId)
    qs.set('free', 'true')
    qs.set('cartId', cart.id)
    router.push(`${pathname}?${qs.toString()}`)
  }, [cart, dispatch, pathname, router, state.config])

  return {
    state,
    method,
    setMethod,
    hasFired,
    setHasFired,
    total,
    actionUrl,
    showPaymentMethods,
    showFreeContinue,
    onPayWithCard,
    onPayWithPaypal,
    onPayWithMbWay,
    onDiscountChanged,
    afterRebuildCart,
    onFreeContinue,
    shouldRebuildBeforePay,
  }
}
